import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { partnerApi } from "@/lib/partner";
import { PartnerShell, PartnerLoading, PartnerEmpty, PartnerError } from "@/components/partner/PartnerShell";

function centsToLocal(amt, cur) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "USD").toUpperCase() })
      .format((Number(amt) || 0) / 100);
  } catch {
    return `${amt}`;
  }
}

function discountLabel(camp) {
  if (!camp) return "—";
  if (camp.discount_type === "percent") return `${camp.percent_off}% off`;
  return `${centsToLocal(camp.amount_off, camp.currency)} off`;
}

function durationLabel(camp) {
  if (!camp) return "";
  if (camp.duration === "repeating") return `for ${camp.duration_in_months} month${camp.duration_in_months === 1 ? "" : "s"}`;
  if (camp.duration === "forever") return "forever";
  return "once";
}

export default function PartnerCodes() {
  const [campaigns, setCampaigns] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCamp, setOpenCamp] = useState(null);
  const [customCode, setCustomCode] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, cd] = await Promise.all([partnerApi.campaigns(), partnerApi.codes()]);
      setCampaigns(Array.isArray(cs?.items) ? cs.items : []);
      setCodes(Array.isArray(cd?.items) ? cd.items : []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "load_failed");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const groupedActive = useMemo(() => codes.filter((c) => c?.active), [codes]);
  const groupedInactive = useMemo(() => codes.filter((c) => !c?.active), [codes]);

  const submitGenerate = async () => {
    if (!openCamp) return;
    setBusy(true);
    try {
      const payload = { campaign_id: openCamp.id };
      if (customCode.trim()) payload.custom_code = customCode.trim().toUpperCase();
      if (maxRedemptions) {
        const n = Number(maxRedemptions);
        if (!Number.isFinite(n) || n <= 0) throw new Error("max_redemptions must be a positive number");
        payload.max_redemptions = Math.floor(n);
      }
      const r = await partnerApi.generateCode(payload);
      toast.success(`Code ${r?.code?.code || ""} created`);
      setOpenCamp(null); setCustomCode(""); setMaxRedemptions("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 503) {
        toast.error("Discount codes are temporarily unavailable — Stripe is not fully configured yet. Please contact support.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.detail) {
        toast.error(detail.detail);
      } else {
        toast.error(e?.message || "Failed to create code");
      }
    } finally {
      setBusy(false);
    }
  };

  const doDeactivate = async (id) => {
    if (!window.confirm("Deactivate this code? Customers can no longer redeem it.")) return;
    try {
      const r = await partnerApi.deactivateCode(id);
      if (r?.stripe_sync === false) {
        toast.warning("Code disabled locally, but Stripe sync failed. Contact support to confirm.");
      } else {
        toast.success("Code deactivated");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.detail || e?.response?.data?.detail || "Failed");
    }
  };

  const copy = (val) => {
    navigator.clipboard.writeText(val);
    toast.success(`Copied ${val}`);
  };

  if (loading) {
    return (
      <PartnerShell testId="partner-codes-page">
        <h1 className="text-3xl font-light tracking-tight">Discount codes</h1>
        <PartnerLoading testId="partner-codes-loading" />
      </PartnerShell>
    );
  }

  if (err) {
    const isGate = err === "not_a_partner" || err === "partner_not_active";
    return (
      <PartnerShell testId="partner-codes-page">
        <h1 className="text-3xl font-light tracking-tight">Discount codes</h1>
        <PartnerError
          testId="partner-codes-error"
          message={
            err === "not_a_partner"
              ? "You’re not a partner yet — apply first to generate discount codes."
              : err === "partner_not_active"
              ? "Your partner account isn’t active yet — codes unlock once your application is approved."
              : "We couldn’t load your codes."
          }
          onRetry={isGate ? undefined : load}
        />
      </PartnerShell>
    );
  }

  return (
    <PartnerShell testId="partner-codes-page">
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-3xl font-light tracking-tight">Discount codes</h1>
        <p className="text-xs text-white/45">
          Codes attribute customers to you at checkout. Attribution is recorded on the first paid invoice.
        </p>
      </div>

      {/* Available campaigns */}
      <div className="mt-8">
        <h2 className="text-sm font-medium text-white/80">Available campaigns</h2>
        {campaigns.length === 0 ? (
          <PartnerEmpty
            testId="partner-codes-empty"
            title="No campaigns are available to you right now"
            sub="Codes will appear here once created for you. Check back later."
          />
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            {campaigns.map((c) => (
              <div key={c?.id} className="rounded-2xl border border-white/10 bg-[#0B0D12] p-4" data-testid={`campaign-${c?.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-white">{c?.name}</p>
                    <p className="mt-0.5 text-xs text-white/50">{discountLabel(c)} · {durationLabel(c)}</p>
                    {c?.notes ? <p className="mt-2 text-xs text-white/45">{c.notes}</p> : null}
                  </div>
                  <button
                    data-testid={`generate-btn-${c?.id}`}
                    onClick={() => setOpenCamp(c)}
                    className="rounded-lg border border-[#D6A653]/50 bg-[#D6A653]/10 px-3 py-1.5 text-xs text-[#D6A653]"
                  >
                    Generate code
                  </button>
                </div>
                <p className="mt-3 text-[11px] text-white/40">
                  Up to {c?.per_partner_max_codes ?? 1} code{(c?.per_partner_max_codes ?? 1) === 1 ? "" : "s"} per partner
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My codes */}
      <div className="mt-10">
        <h2 className="text-sm font-medium text-white/80">My codes</h2>
        {codes.length === 0 ? (
          <p className="mt-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-sm text-white/50" data-testid="no-codes">
            You haven’t generated any codes yet.
          </p>
        ) : (
          <>
            <CodesTable rows={groupedActive} title="Active" onCopy={copy} onDeactivate={doDeactivate} testId="my-codes-active" />
            {groupedInactive.length > 0 && (
              <CodesTable rows={groupedInactive} title="Inactive" onCopy={copy} testId="my-codes-inactive" />
            )}
          </>
        )}
      </div>

      {openCamp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="generate-code-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
            <h3 className="text-lg font-light text-white">Generate a code</h3>
            <p className="mt-1 text-xs text-white/50">
              For campaign <span className="text-white/80">{openCamp?.name}</span> · {discountLabel(openCamp)} · {durationLabel(openCamp)}
            </p>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-white/50">Custom code (optional)</span>
                <input
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                  placeholder="e.g. ACME-VIP20"
                  maxLength={32}
                  data-testid="generate-custom-code"
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#D6A653]/50"
                />
                <span className="mt-1 block text-[10px] text-white/40">Leave blank to auto-generate. A–Z 0–9 only, 4–32 chars.</span>
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-white/50">Max redemptions (optional)</span>
                <input
                  value={maxRedemptions}
                  onChange={(e) => setMaxRedemptions(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="Leave blank for unlimited"
                  data-testid="generate-max-redemptions"
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#D6A653]/50"
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => { setOpenCamp(null); setCustomCode(""); setMaxRedemptions(""); }}
                data-testid="generate-cancel"
                className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70"
              >
                Cancel
              </button>
              <button
                onClick={submitGenerate}
                disabled={busy}
                data-testid="generate-confirm"
                className="rounded-lg bg-[#D6A653] px-4 py-2 text-sm text-black disabled:opacity-60"
              >
                {busy ? "Creating…" : "Create code"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PartnerShell>
  );
}

function CodesTable({ rows, title, onCopy, onDeactivate, testId }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10 bg-[#0B0D12]" data-testid={testId}>
      <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-white/45">{title}</div>
      <table className="min-w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wider text-white/40">
          <tr>
            <th className="px-4 py-2">Code</th>
            <th className="px-4 py-2">Discount</th>
            <th className="px-4 py-2 text-right">Redeemed</th>
            <th className="px-4 py-2 text-right">Max</th>
            <th className="px-4 py-2">Last used</th>
            <th className="px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody className="text-white/80">
          {rows.map((r) => (
            <tr key={r?.id} className="border-t border-white/5" data-testid={`code-row-${r?.id}`}>
              <td className="px-4 py-2 font-mono text-[#D6A653]">{r?.code}</td>
              <td className="px-4 py-2 text-xs text-white/70">{discountLabel(r?.campaign)} · {durationLabel(r?.campaign)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{r?.times_redeemed_cached ?? 0}</td>
              <td className="px-4 py-2 text-right tabular-nums text-white/50">{r?.max_redemptions ?? "∞"}</td>
              <td className="px-4 py-2 text-xs text-white/50">{r?.last_redeemed_at ? new Date(r.last_redeemed_at).toLocaleDateString() : "—"}</td>
              <td className="px-4 py-2">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => onCopy(r?.code)}
                    data-testid={`copy-code-${r?.id}`}
                    className="rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5"
                  >
                    Copy
                  </button>
                  {onDeactivate && (
                    <button
                      onClick={() => onDeactivate(r?.id)}
                      data-testid={`deactivate-code-${r?.id}`}
                      className="rounded-lg border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
